// src/pages/expenses/SitePurchase.js
// Expense Module — v2 — 27 May 2026
// Governed: company_id on all queries, service layer, enum-based categories
// v3 — Costing Card Part A: project_id/project_name tagging added (site-linked
// projects fetched via fetchProjectsForSite(), auto-select if exactly one).

import { useState, useEffect, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, addDoc, getDocs,
  query, where, orderBy, serverTimestamp
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { fetchProjectsForSite, autoSelectSiteIfOnlyOne } from "../../services/clientService";

// ── ENUMS ──────────────────────────────────────────────────────────────────
const EXPENSE_CATEGORY = {
  TRAVEL:            "travel",
  FOOD:              "food",
  LABOUR:            "labour",
  VEHICLE_REPAIR:    "vehicle_repair",
  TOOL_REPAIR:       "tool_repair",
  ACCOMMODATION:     "accommodation",
  SITE_CONSUMABLES:  "site_consumables",
  MISC:              "misc",
};

const EXPENSE_CATEGORY_LABELS = {
  travel:            "Travel / Fuel",
  food:              "Food / Meals",
  labour:            "Labour / Helper",
  vehicle_repair:    "Vehicle Repair / Maintenance",
  tool_repair:       "Tool Repair",
  accommodation:     "Accommodation",
  site_consumables:  "Site Consumables",
  misc:              "Miscellaneous",
};

const EXPENSE_TYPE = {
  ALREADY_SPENT: "already_spent",
  NEED_ADVANCE:  "need_advance",
};

const EXPENSE_TYPE_LABELS = {
  already_spent: "Already Spent (Reimbursement)",
  need_advance:  "Need Advance",
};

const EXPENSE_STATUS_UI = {
  pending:  { label: "Pending",  bg: "#fef3c7", color: "#92400e" },
  approved: { label: "Approved", bg: "#d1fae5", color: "#065f46" },
  rejected: { label: "Rejected", bg: "#fee2e2", color: "#991b1b" },
};

const storage = getStorage();

export default function SitePurchase() {
  const user      = JSON.parse(localStorage.getItem("user") || "{}");
  const companyId = user.company_id || "";
  const isSM      = ["sm", "owner", "manager"].includes(user.role);
  const isTech    = user.role === "technician" || user.role === "tech";

  // ── TABS ──────────────────────────────────────────────────────────────
  const [tab, setTab] = useState("list"); // "list" | "create"

  // ── LIST STATE ─────────────────────────────────────────────────────────
  const [expenses,     setExpenses]     = useState([]);
  const [listLoading,  setListLoading]  = useState(true);

  // ── FORM STATE ─────────────────────────────────────────────────────────
  const [sites,        setSites]        = useState([]);
  const [saving,       setSaving]       = useState(false);
  const [photoLoading, setPhotoLoading] = useState(false);
  const fileRef = useRef(null);

  // Costing Card Part A — projects available for the currently-selected site.
  // Refetched whenever form.site_id changes; auto-selected if exactly one.
  const [siteProjects,        setSiteProjects]        = useState([]);
  const [loadingSiteProjects, setLoadingSiteProjects]  = useState(false);

  const emptyForm = {
    category:     "",
    amount:       "",
    description:  "",
    site_id:      "",
    site_name:    "",
    project_id:   "",
    project_name: "",
    expense_type: "",
    bill_url:     "",
  };
  const [form, setForm] = useState(emptyForm);

  // ── LOAD ───────────────────────────────────────────────────────────────
  useEffect(() => {
    loadExpenses();
    loadSites();
  }, []);

  const loadExpenses = async () => {
    setListLoading(true);
    try {
      let q;
      const myId = user.uid || user.id || "";

      if (isSM) {
        // SM/Owner/Manager — all company expenses
        q = query(
          collection(db, "site_purchase_requests"),
          where("company_id", "==", companyId),
          where("type",       "==", "expense"),
          orderBy("created_at", "desc")
        );
      } else {
        // Tech/Employee — only own expenses
        // Try uid first — fallback handled client-side
        q = query(
          collection(db, "site_purchase_requests"),
          where("company_id",   "==", companyId),
          where("type",         "==", "expense"),
          where("requested_by", "==", myId),
          orderBy("created_at", "desc")
        );
      }

      const snap = await getDocs(q);
      let list = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Client-side safety filter for non-SM
      // Handles case where requested_by stored as uid or id
      if (!isSM) {
        list = list.filter(exp =>
          exp.requested_by === user.uid ||
          exp.requested_by === user.id  ||
          exp.requested_by === user.docId
        );
      }

      setExpenses(list);
    } catch (err) {
      console.error(err);
    } finally {
      setListLoading(false);
    }
  };

  const loadSites = async () => {
    try {
      const q = query(
        collection(db, "sites"),
        where("company_id", "==", companyId)
      );
      const snap = await getDocs(q);
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.site_name || "").localeCompare(b.site_name || ""));
      setSites(list);
    } catch (err) {
      console.error(err);
    }
  };

  // Costing Card Part A — whenever a site is selected, fetch its linked
  // projects. Exactly one → auto-select silently. Zero → leave project
  // fields blank (site-only expense, no project attribution — still valid,
  // e.g. general site upkeep not tied to a specific project). 2+ → show a
  // manual picker (rendered below).
  const loadProjectsForSite = async (site_id) => {
    if (!site_id) {
      setSiteProjects([]);
      return;
    }
    setLoadingSiteProjects(true);
    try {
      const projects = await fetchProjectsForSite(companyId, site_id);
      setSiteProjects(projects);
      const onlyProject = autoSelectSiteIfOnlyOne(projects); // same "exactly one" helper works for any list
      if (onlyProject) {
        setForm(f => ({ ...f, project_id: onlyProject.id, project_name: onlyProject.project_name || "" }));
      } else {
        setForm(f => ({ ...f, project_id: "", project_name: "" }));
      }
    } catch (err) {
      console.error("Failed to load projects for site:", err);
      setSiteProjects([]);
    } finally {
      setLoadingSiteProjects(false);
    }
  };

  // ── PHOTO UPLOAD ───────────────────────────────────────────────────────
  const handleBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    try {
      const storageRef = ref(storage, `expenses/${companyId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setForm(f => ({ ...f, bill_url: url }));
    } catch (err) {
      alert("Photo upload failed: " + err.message);
    } finally {
      setPhotoLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // ── SUBMIT ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.category)    { alert("Category required"); return; }
    if (!form.amount || Number(form.amount) <= 0) { alert("Valid amount required"); return; }
    if (!form.description.trim()) { alert("Description required"); return; }
    if (!form.expense_type) { alert("Please select whether this is already spent or a need for advance"); return; }

    setSaving(true);
    try {
      await addDoc(collection(db, "site_purchase_requests"), {
        company_id:    companyId,
        requested_by:      user.uid || user.id || "",
        requested_by_id:   user.uid || user.id || "",   // backup field
        requested_by_name: user.name || "",
        requested_by_role: user.role || "",
        type:          "expense",
        category:      form.category,
        amount:        Number(form.amount),
        description:   form.description.trim(),
        site_id:       form.site_id || "",
        site_name:     form.site_name || "",
        project_id:    form.project_id || "",
        project_name:  form.project_name || "",
        expense_type:  form.expense_type,
        bill_url:      form.bill_url || "",
        status:        "pending",
        created_at:    serverTimestamp(),
        updated_at:    serverTimestamp(),
      });

      setForm(emptyForm);
      setSiteProjects([]);
      setTab("list");
      loadExpenses();
    } catch (err) {
      alert(err.message || "Failed to save expense");
    } finally {
      setSaving(false);
    }
  };

  // ── RENDER ─────────────────────────────────────────────────────────────
  return (
    <div className="p-4 pb-24 bg-gray-50 min-h-screen max-w-2xl mx-auto">

      {/* HEADER */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold text-slate-800">Expenses</h1>
        {tab === "list" ? (
          <button onClick={() => setTab("create")}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold">
            + New Expense
          </button>
        ) : (
          <button onClick={() => { setTab("list"); setForm(emptyForm); setSiteProjects([]); }}
            className="text-sm text-slate-500 hover:text-slate-800">
            ← Back
          </button>
        )}
      </div>

      {/* ── LIST VIEW ── */}
      {tab === "list" && (
        <>
          {listLoading ? (
            <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
          ) : expenses.length === 0 ? (
            <div className="text-center mt-16">
              <div className="text-4xl mb-3">🧾</div>
              <p className="text-slate-500 font-medium">No expenses yet</p>
              <p className="text-slate-400 text-sm mt-1">Tap + New Expense to add</p>
            </div>
          ) : (
            <div className="space-y-3">
              {expenses.map(exp => {
                const statusUI = EXPENSE_STATUS_UI[exp.status] || EXPENSE_STATUS_UI.pending;
                return (
                  <div key={exp.id}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4"
                    style={{ borderLeft: `4px solid ${statusUI.color}` }}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-slate-800">
                            {EXPENSE_CATEGORY_LABELS[exp.category] || exp.category}
                          </p>
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full"
                            style={{ background: statusUI.bg, color: statusUI.color }}>
                            {statusUI.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">{exp.description}</p>
                        {exp.requested_by_name && isSM && (
                          <p className="text-xs text-slate-400 mt-0.5">
                            👤 {exp.requested_by_name}
                          </p>
                        )}
                        <div className="flex items-center gap-3 mt-1 flex-wrap">
                          <span className={`text-xs font-semibold ${exp.expense_type === "need_advance" ? "text-amber-600" : "text-slate-400"}`}>
                            {exp.expense_type === "need_advance" ? "💵 Need Advance" : "🧾 Already Spent"}
                          </span>
                          {exp.site_name && (
                            <span className="text-xs text-blue-500">📍 {exp.site_name}</span>
                          )}
                          {exp.project_name && (
                            <span className="text-xs text-purple-500">🏗 {exp.project_name}</span>
                          )}
                          {exp.bill_url && (
                            <a href={exp.bill_url} target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="text-xs text-blue-500 underline">
                              📄 View Bill
                            </a>
                          )}
                        </div>
                      </div>
                      <p className="text-lg font-black text-slate-800 ml-3 shrink-0">
                        ₹{Number(exp.amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ── CREATE VIEW ── */}
      {tab === "create" && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">

          {/* Category */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Category *
            </label>
            <select value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">Select category</option>
              {Object.entries(EXPENSE_CATEGORY_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Amount (₹) *
            </label>
            <input type="number" placeholder="0" min="1"
              value={form.amount}
              onChange={e => {
                const val = e.target.value;
                if (val === "" || Number(val) > 0) {
                  setForm(f => ({ ...f, amount: val }));
                }
              }}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Description *
            </label>
            <textarea rows={3} placeholder="What was this expense for?"
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none resize-none focus:ring-2 focus:ring-blue-300" />
          </div>

          {/* Expense Type */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Expense Type *
            </label>
            <select value={form.expense_type}
              onChange={e => setForm(f => ({ ...f, expense_type: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">Select type</option>
              {Object.entries(EXPENSE_TYPE_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          {/* Link to Site (optional) */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Site (optional)
            </label>
            <select value={form.site_id}
              onChange={e => {
                const selected = sites.find(s => s.id === e.target.value);
                setForm(f => ({
                  ...f,
                  site_id: e.target.value,
                  site_name: selected?.site_name || "",
                  project_id: "",
                  project_name: "",
                }));
                loadProjectsForSite(e.target.value);
              }}
              className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
              <option value="">No site link</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>
                  {s.site_name} {s.client_name ? `— ${s.client_name}` : ""}
                </option>
              ))}
            </select>
          </div>

          {/* Link to Project (optional) — Costing Card Part A.
              Only shown once a site is selected. Auto-selected silently if the
              site has exactly one project (see loadProjectsForSite); a manual
              picker appears only when there are 2+ projects to choose from. */}
          {form.site_id && (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                Project (optional)
              </label>
              {loadingSiteProjects ? (
                <p className="text-xs text-slate-400">Loading projects for this site...</p>
              ) : siteProjects.length === 0 ? (
                <p className="text-xs text-slate-400">No projects linked to this site.</p>
              ) : siteProjects.length === 1 ? (
                <p className="text-xs text-emerald-600 font-semibold">
                  ✓ Auto-linked to {form.project_name || siteProjects[0].project_name} (only project for this site)
                </p>
              ) : (
                <select value={form.project_id}
                  onChange={e => {
                    const selected = siteProjects.find(p => p.id === e.target.value);
                    setForm(f => ({
                      ...f,
                      project_id: e.target.value,
                      project_name: selected?.project_name || "",
                    }));
                  }}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none bg-white focus:ring-2 focus:ring-blue-300">
                  <option value="">No project link (site-only expense)</option>
                  {siteProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Bill Photo */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
              Bill / Receipt (optional)
            </label>
            <input ref={fileRef} type="file" accept="image/*,application/pdf"
              onChange={handleBillUpload} className="hidden" />
            <button onClick={() => fileRef.current?.click()}
              disabled={photoLoading}
              className="w-full py-2.5 rounded-xl border border-dashed border-slate-300 text-sm text-slate-500 hover:border-blue-400 hover:text-blue-500 transition">
              {photoLoading ? "Uploading..." : form.bill_url ? "✅ Bill uploaded — tap to change" : "📷 Upload Bill / Receipt"}
            </button>
            {form.bill_url && (
              <a href={form.bill_url} target="_blank" rel="noreferrer"
                className="text-xs text-blue-500 underline mt-1 block">
                View uploaded bill →
              </a>
            )}
          </div>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={saving}
            className="w-full py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-40">
            {saving ? "Saving..." : "Submit Expense"}
          </button>

        </div>
      )}
    </div>
  );
}
